import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { digitalKhata, stores as storesApi, api, deliverWhatsApp } from '../lib/api';
import { toast } from 'sonner';
import { Search, Wallet, TrendingUp, Clock, MessageCircle, UserCheck, UserX,
  Plus, Loader2, IndianRupee, Calendar, WifiOff, CheckCircle2, X, Send, Zap, SkipForward, Pause, Play } from 'lucide-react';
import KhataCustomerRating from './KhataCustomerRating';
import { queueGet, queueSet, flushKhataQueue } from '../lib/khataQueue';
import { receiptWaUrl } from '../lib/khataReceipt';
import { fmtStamp } from './LiveClock';

// ---------- Local cache (works offline for slow 2G/3G villages) ----------
const CACHE_KEY = 'mandispot_khata_dashboard_v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;   // keep for 7 days even if offline

const cacheGet = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, data } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return { at, data };
  } catch { return null; }
};
const cacheSet = (data) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data })); } catch { /* quota */ }
};


const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtAge = (days) => days === 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;

export default function KhataManagement() {
  const [data, setData]         = useState(null);        // {customers, totals, stores}
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [staleCache, setStaleCache] = useState(false);
  const [offline, setOffline]   = useState(!navigator.onLine);
  const [query, setQuery]       = useState('');
  const [sort, setSort]         = useState('balance_desc');
  const [storeFilter, setStoreFilter] = useState('');
  const [pending, setPending]   = useState([]);           // optimistic queue count
  const [addOpen, setAddOpen]   = useState(false);
  const [drawer, setDrawer]     = useState(null);         // customer detail drawer
  const [bulkOpen, setBulkOpen] = useState(false);        // bulk nudge wizard

  // ---------- Load: cache-first, then network ----------
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { sort };
      if (storeFilter) params.store_id = storeFilter;
      const fresh = await digitalKhata.dashboard(params);
      setData(fresh);
      setRefreshedAt(Date.now());
      setStaleCache(false);
      cacheSet(fresh);
    } catch (e) {
      // Fall back to cache silently
      const cached = cacheGet();
      if (cached) { setData(cached.data); setStaleCache(true); }
      else { toast.error('Could not load Khata'); }
    } finally { setLoading(false); }
  }, [sort, storeFilter]);

  // Cache-first render on mount
  useEffect(() => {
    const cached = cacheGet();
    if (cached) { setData(cached.data); setStaleCache(true); setLoading(false); }
    load({ silent: !!cached });
  }, [load]);

  // Network status listener
  useEffect(() => {
    const on = () => { setOffline(false); flushQueue(); };
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    setPending(queueGet());
    window.__msKhataMounted = true;
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.__msKhataMounted = false;
    };
    /* eslint-disable-next-line */
  }, []);

  // Retry queue flush
  const flushQueue = async () => {
    const before = queueGet().length;
    if (!before) { setPending([]); return; }
    const { remaining } = await flushKhataQueue();
    setPending(remaining);
    if (remaining.length === 0) { toast.success('All offline entries synced ✔'); await load({ silent: true }); }
    else toast.info(`${before - remaining.length}/${before} entries synced`);
  };

  // ---------- Client-side search (works even offline) ----------
  const filtered = useMemo(() => {
    if (!data?.customers) return [];
    let out = data.customers;
    if (query.trim()) {
      const n = query.trim().toLowerCase();
      out = out.filter(c => (c.customer_name || '').toLowerCase().includes(n) || c.customer_phone.includes(n));
    }
    if (sort === 'balance_desc')      out = [...out].sort((a, b) => b.balance - a.balance);
    else if (sort === 'oldest_due')   out = [...out].sort((a, b) => (b.days_due - a.days_due) || (b.balance - a.balance));
    else if (sort === 'recent')       out = [...out].sort((a, b) => (b.last_txn_at || '').localeCompare(a.last_txn_at || ''));
    return out;
  }, [data, query, sort]);

  // ---------- Optimistic actions ----------
  const optimisticRepay = async (customer, amount) => {
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    const clientRef = `repay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    // 1) Update local state instantly
    setData(prev => {
      if (!prev) return prev;
      const nxt = { ...prev, customers: prev.customers.map(c =>
        (c.store_id === customer.store_id && c.customer_phone === customer.customer_phone)
          ? { ...c, balance: Math.max(0, c.balance - amount), _optimistic: true }
          : c
      )};
      nxt.totals = { ...nxt.totals, outstanding: Math.max(0, (nxt.totals.outstanding || 0) - amount) };
      cacheSet(nxt);
      return nxt;
    });
    toast.success(`Recorded ₹${amount} received (syncing…)`);
    // 2) Send in background (queue on failure)
    const payload = { store_id: customer.store_id, customer_phone: customer.customer_phone, amount, client_ref: clientRef };
    try {
      await digitalKhata.repay(payload);
      await load({ silent: true });
    } catch {
      const q = queueGet(); q.push({ kind: 'repay', payload, at: Date.now() }); queueSet(q); setPending(q);
      toast.info('Offline — saved locally, will sync when online');
    }
  };

  const openNudge = async (c) => {
    try {
      const r = await digitalKhata.nudge(c.store_id, c.customer_phone);
      const how = await deliverWhatsApp({ phone: c.customer_phone, message: r.message, wa_url: r.wa_url });
      if (how === 'sent') toast.success('WhatsApp reminder sent');
    } catch { toast.error('Could not build WhatsApp link'); }
  };

  // ---------- Render ----------
  return (
    <section data-testid="khata-management" className="space-y-4">
      {/* Header + connectivity */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#7A4A00] text-white grid place-items-center flex-shrink-0"><Wallet className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#7A4A00]">Digital Khata · Udhaar Ledger</div>
          <h2 className="font-heading font-bold text-2xl">Who owes you, and how much</h2>
          <p className="text-xs text-ink/60 mt-1">Search instantly, sort by balance or oldest due, and nudge on WhatsApp. Works offline — records sync when your signal returns.</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {offline && <span data-testid="khata-offline-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#FFF3E0] text-[#9A4500] px-2 py-1 rounded-full font-bold"><WifiOff className="w-3 h-3"/>Offline</span>}
          {pending.length > 0 && <span data-testid="khata-pending-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#E3F2FD] text-[#0D47A1] px-2 py-1 rounded-full font-bold"><Loader2 className="w-3 h-3 animate-spin"/>{pending.length} pending sync</span>}
          {staleCache && !offline && <span className="text-[10px] text-ink/40">showing cached · refreshing…</span>}
          <button data-testid="khata-add-udhaar" onClick={() => setAddOpen(true)} className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5"><Plus className="w-4 h-4"/>New udhaar</button>
          {(data?.totals?.outstanding || 0) > 0 && (
            <button
              data-testid="khata-bulk-nudge-btn"
              onClick={() => setBulkOpen(true)}
              className="rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#075E54] text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 shadow-md"
              title="Send monthly reminder WhatsApp to all customers with outstanding balances"
            >
              <Zap className="w-4 h-4"/>Bulk WhatsApp Nudge
            </button>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3">
        <div data-testid="khata-total-outstanding" className="rounded-2xl border-2 border-[#F2C97A] bg-[#FFF3E0] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A4500]">Outstanding balance</div>
          <div className="font-heading font-black text-3xl mt-1 text-[#9A4500] tabular-nums">{inr(data?.totals?.outstanding)}</div>
          <div className="text-[11px] text-[#9A4500]/70 mt-0.5">Across {data?.totals?.customers || 0} customers</div>
        </div>
        <div className="rounded-2xl border-2 border-[#A5D6A7] bg-[#E8F5E9] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1B5E20]">Trusted customers</div>
          <div className="font-heading font-black text-3xl mt-1 text-[#1B5E20]">{data?.totals?.trusted || 0}</div>
          <div className="text-[11px] text-[#1B5E20]/70 mt-0.5">Can Pay-on-Khata at checkout</div>
        </div>
        <div className="rounded-2xl border-2 border-[#90CAF9] bg-[#E3F2FD] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0D47A1]">Stores</div>
          <div className="font-heading font-black text-3xl mt-1 text-[#0D47A1]">{data?.stores?.length || 0}</div>
          <div className="text-[11px] text-[#0D47A1]/70 mt-0.5">Each has its own Khata book</div>
        </div>
      </div>

      {/* Auto-schedule config */}
      <ScheduleConfigCard/>

      {/* Search + Sort */}
      <div className="card-soft p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"/>
          <input
            data-testid="khata-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or phone…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-white text-sm outline-none focus:border-[#7A4A00] focus:ring-2 focus:ring-[#7A4A00]/20"
          />
        </div>
        {data?.stores?.length > 1 && (
          <select
            data-testid="khata-store-filter"
            value={storeFilter}
            onChange={e => setStoreFilter(e.target.value)}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"
          >
            <option value="">All stores</option>
            {data.stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div className="flex gap-1">
          {[
            { k: 'balance_desc', label: 'Highest Balance', Icon: TrendingUp },
            { k: 'oldest_due',   label: 'Oldest Due',      Icon: Clock },
            { k: 'recent',       label: 'Recent',          Icon: Calendar },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              data-testid={`khata-sort-${k}`}
              onClick={() => setSort(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition ${sort === k ? 'bg-[#7A4A00] text-white border-[#7A4A00]' : 'bg-white text-ink/70 border-border hover:border-[#7A4A00]/40'}`}
            >
              <Icon className="w-3.5 h-3.5"/>{label}
            </button>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div className="card-soft overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-ink/60">
            <Wallet className="w-10 h-10 mx-auto text-ink/30"/>
            <div className="font-heading font-bold text-base mt-3">
              {query ? `No customer matches "${query}"` : 'No khata customers yet'}
            </div>
            {!query && <div className="text-xs text-ink/50 mt-1">Add a trusted customer to enable Pay-on-Khata for them.</div>}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map(c => (
              <KhataRow
                key={`${c.store_id}_${c.customer_phone}`}
                c={c}
                onNudge={() => openNudge(c)}
                onRepay={(amt) => optimisticRepay(c, amt)}
                onOpen={() => setDrawer(c)}
              />
            ))}
          </div>
        )}
      </div>

      {addOpen && <AddUdhaarModal stores={data?.stores || []} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await load({ silent: true }); }} setPending={setPending}/>}
      {drawer && <CustomerDetailDrawer customer={drawer} onClose={() => setDrawer(null)} onChanged={() => load({ silent: true })}/>}
      {bulkOpen && <BulkNudgeWizard storeFilter={storeFilter} onClose={() => setBulkOpen(false)}/>}
    </section>
  );
}

// -------- Row --------
function KhataRow({ c, onNudge, onRepay, onOpen }) {
  const [repayInput, setRepayInput] = useState('');
  const [openInput, setOpenInput] = useState(false);
  const badge = c.balance > 0 ? 'bg-[#FFF3E0] text-[#9A4500]' : 'bg-[#E8F5E9] text-[#1B5E20]';
  const submit = () => {
    const n = parseFloat(repayInput);
    if (!n || n <= 0) { setOpenInput(false); return; }
    onRepay(n);
    setRepayInput(''); setOpenInput(false);
  };
  return (
    <div data-testid={`khata-row-${c.customer_phone}`} className={`p-4 flex items-center gap-3 flex-wrap hover:bg-[#FFF8E1]/40 ${c._optimistic ? 'bg-[#E8F5E9]/40' : ''}`}>
      <div className="w-10 h-10 rounded-full bg-[#F5EFE0] text-[#7A4A00] grid place-items-center font-heading font-black">
        {(c.customer_name || '?').slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <button onClick={onOpen} className="w-full text-left group">
          <div className="font-heading font-bold text-base truncate group-hover:text-[#7A4A00]">
            {c.customer_name || 'Unknown'}
            {c.trusted && <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#E8F5E9] text-[#1B5E20] px-1.5 py-0.5 rounded-full">Trusted</span>}
            {c._optimistic && <Loader2 className="inline w-3 h-3 ml-2 animate-spin text-[#1B5E20]"/>}
          </div>
          <div className="text-xs text-ink/60">
            📱 {c.customer_phone}
            {c.days_due > 0 && <span className="ml-2 text-[#9A4500]">· Oldest due {fmtAge(c.days_due)}</span>}
            <span className="ml-2">· {c.txn_count} txns</span>
          </div>
        </button>
        <KhataCustomerRating storeId={c.store_id} customerPhone={c.customer_phone}/>
      </div>
      <div className="text-right">
        <span className={`inline-block rounded-lg px-3 py-1.5 font-heading font-black text-lg tabular-nums ${badge}`}>{inr(c.balance)}</span>
        {c.credit_limit > 0 && <div className="text-[10px] text-ink/50 mt-0.5">Limit {inr(c.credit_limit)}</div>}
      </div>
      <div className="flex gap-2 items-center">
        {openInput ? (
          <div className="flex items-center gap-1">
            <input
              data-testid={`khata-repay-input-${c.customer_phone}`}
              autoFocus type="number" placeholder="₹ received"
              value={repayInput}
              onChange={e => setRepayInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submit()}
              className="w-24 rounded-lg border border-border bg-white px-2 py-1.5 text-sm"
            />
            <button data-testid={`khata-repay-confirm-${c.customer_phone}`} onClick={submit} className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-3 py-1.5 text-xs font-bold">Save</button>
            <button onClick={() => setOpenInput(false)} className="text-ink/40 hover:text-ink/60"><X className="w-4 h-4"/></button>
          </div>
        ) : (
          <>
            {c.balance > 0 && (
              <button data-testid={`khata-mark-paid-${c.customer_phone}`} onClick={() => setOpenInput(true)} className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5"><IndianRupee className="w-3 h-3"/>Paisa aaya</button>
            )}
            <button data-testid={`khata-nudge-${c.customer_phone}`} onClick={onNudge} className="rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5"><MessageCircle className="w-3 h-3"/>Nudge</button>
          </>
        )}
      </div>
    </div>
  );
}

// -------- Add Udhaar / Trust modal --------
function AddUdhaarModal({ stores, onClose, onSaved, setPending }) {
  const [tab, setTab]     = useState('trust');
  const [form, setForm]   = useState({ store_id: stores[0]?.id || '', customer_name: '', customer_phone: '', credit_limit: '0', amount: '', note: '',
                                        id_type: 'aadhaar', id_number: '', id_photo_url: '' });
  const [busy, setBusy]   = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadKyc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, id_photo_url: res.data.url || res.data.file_url || res.data.path }));
      toast.success('ID photo uploaded');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!/^[6-9]\d{9}$/.test(form.customer_phone)) return toast.error('Phone must be a valid 10-digit Indian mobile (6-9)');
    if (!form.store_id) return toast.error('Pick a store');
    setBusy(true);
    const clientRef = `${tab}_${Date.now()}`;
    try {
      if (tab === 'trust') {
        // 1) Upload customer KYC first
        if (!form.id_photo_url || !form.id_number) {
          setBusy(false);
          return toast.error('Please upload customer ID photo + enter ID number');
        }
        await digitalKhata.uploadCustomerKyc({
          store_id: form.store_id, customer_phone: form.customer_phone,
          id_type: form.id_type, id_number: form.id_number.replace(/\s+/g, ''),
          id_photo_url: form.id_photo_url,
        });
        // 2) Then mark as trusted
        await digitalKhata.trust({
          store_id: form.store_id, customer_phone: form.customer_phone,
          customer_name: form.customer_name, credit_limit: parseFloat(form.credit_limit) || 0, trusted: true,
        });
        toast.success('✅ KYC verified + customer added to Khata trust list');
      } else {
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) { setBusy(false); return toast.error('Enter a valid amount'); }
        try {
          await digitalKhata.manualDebit({
            store_id: form.store_id, customer_phone: form.customer_phone,
            customer_name: form.customer_name, amount: amt, note: form.note, client_ref: clientRef,
          });
          toast.success(`Udhaar of ₹${amt} recorded`);
        } catch {
          const q = queueGet(); q.push({ kind: 'debit', payload: {
            store_id: form.store_id, customer_phone: form.customer_phone, customer_name: form.customer_name,
            amount: amt, note: form.note, client_ref: clientRef,
          }, at: Date.now() }); queueSet(q); setPending(q);
          toast.info('Offline — saved locally, will sync when online');
        }
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-5" onClick={e => e.stopPropagation()} data-testid="add-udhaar-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-black text-xl">Add customer / Record udhaar</h3>
          <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5"/></button>
        </div>
        <div className="flex gap-1 mb-4 border-b border-border">
          {[
            { k: 'trust', label: '✓ Add trusted customer', desc: 'Enables Pay-on-Khata' },
            { k: 'debit', label: '₹ Record counter udhaar', desc: 'Offline sale on credit' },
          ].map(({ k, label }) => (
            <button
              key={k}
              data-testid={`add-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 transition ${tab === k ? 'border-[#7A4A00] text-[#7A4A00]' : 'border-transparent text-ink/50 hover:text-ink'}`}
            >{label}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="udh-store" value={form.store_id} onChange={e => setForm({ ...form, store_id: e.target.value })} className="col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm">
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input data-testid="udh-name" placeholder="Customer name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="udh-phone" placeholder="10-digit mobile" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          {tab === 'trust' ? (
            <>
              <input data-testid="udh-limit" type="number" placeholder="Credit limit ₹ (0 = unlimited)" value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: e.target.value })} className="col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>

              {/* KYC block — MANDATORY to enable Pay-on-Khata */}
              <div className="col-span-2 mt-3 rounded-xl border-2 border-dashed border-[#7A4A00] bg-[#FFF3E0] p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#7A4A00] mb-2">🔒 Customer KYC (required for fraud prevention)</div>
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="udh-id-type" value={form.id_type} onChange={e => setForm({ ...form, id_type: e.target.value })} className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
                    <option value="aadhaar">Aadhaar (12 digits)</option>
                    <option value="voter">Voter ID</option>
                    <option value="driving_license">Driving Licence</option>
                    <option value="pan">PAN (ABCDE1234F)</option>
                  </select>
                  <input data-testid="udh-id-number" placeholder={form.id_type === 'aadhaar' ? '5555 1111 3333' : 'ID number'} value={form.id_number} onChange={e => setForm({ ...form, id_number: e.target.value })} className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
                  <label className="col-span-2 flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed border-[#7A4A00]/40 bg-white px-3 py-3 text-sm hover:border-[#7A4A00]">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#7A4A00]"/> : <UserCheck className="w-4 h-4 text-[#7A4A00]"/>}
                    <span className="flex-1 text-ink/70">
                      {form.id_photo_url ? '✅ ID photo uploaded — tap to replace' : 'Tap to upload ID photo…'}
                    </span>
                    <input data-testid="udh-id-photo" type="file" accept="image/*,application/pdf" hidden onChange={e => uploadKyc(e.target.files?.[0])}/>
                  </label>
                  <p className="col-span-2 text-[10px] text-[#7A4A00]/70 leading-snug">
                    🔒 <b>Privacy:</b> the full ID number is hidden after saving — only the last 4 digits are shown back to you. Photo is stored securely in the Emergent Object Store.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <input data-testid="udh-amount" type="number" placeholder="Amount ₹" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
              <input data-testid="udh-note" placeholder="Note (optional)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
            </>
          )}
        </div>
        <button
          data-testid="udh-save"
          disabled={busy}
          onClick={save}
          className="mt-4 w-full rounded-full bg-[#7A4A00] hover:bg-[#5C3800] text-white px-5 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : (tab === 'trust' ? <UserCheck className="w-4 h-4"/> : <Plus className="w-4 h-4"/>)}
          {tab === 'trust' ? 'Add to trust list' : 'Record udhaar'}
        </button>
      </div>
    </div>
  );
}

// -------- Customer detail drawer --------
function CustomerDetailDrawer({ customer, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    digitalKhata.customerDetail(customer.store_id, customer.customer_phone).then(setDetail).catch(() => {});
  }, [customer]);

  const toggleTrust = async () => {
    try {
      if (customer.trusted) {
        await digitalKhata.untrust(customer.store_id, customer.customer_phone);
        toast.success('Removed from trust list');
      } else {
        await digitalKhata.trust({
          store_id: customer.store_id, customer_phone: customer.customer_phone,
          customer_name: customer.customer_name, credit_limit: customer.credit_limit || 0, trusted: true,
        });
        toast.success('Added to trust list — they can now Pay-on-Khata');
      }
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={e => e.stopPropagation()} data-testid="khata-drawer">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-black text-xl">{customer.customer_name}</h3>
          <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5"/></button>
        </div>
        <div className="text-sm text-ink/60">📱 {customer.customer_phone} · {customer.store_name}</div>
        <div className="mt-3 rounded-2xl bg-[#FFF3E0] p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#9A4500] font-bold">Outstanding</div>
          <div className="font-heading font-black text-3xl text-[#9A4500] mt-1">{inr(detail?.balance ?? customer.balance)}</div>
          {customer.credit_limit > 0 && (
            <div className="text-xs text-ink/60 mt-1">Credit limit: {inr(customer.credit_limit)} · Available: {inr(Math.max(0, customer.credit_limit - (detail?.balance ?? customer.balance)))}</div>
          )}
        </div>
        <button data-testid="khata-toggle-trust" onClick={toggleTrust} className={`mt-3 w-full rounded-full px-4 py-2 text-sm font-bold inline-flex items-center justify-center gap-2 ${customer.trusted ? 'bg-white text-[#BF360C] border border-[#BF360C]' : 'bg-[#1B5E20] text-white'}`}>
          {customer.trusted ? <><UserX className="w-4 h-4"/>Remove from trust list</> : <><UserCheck className="w-4 h-4"/>Add to trust list</>}
        </button>
        <div className="mt-5">
          <div className="font-heading font-bold text-base mb-2">Ledger ({detail?.ledger?.length || 0})</div>
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {(detail?.ledger || []).map(l => (
              <div key={l.id} className="flex items-start gap-3 rounded-lg border border-border p-3">
                <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-xs font-bold ${l.type === 'debit' ? 'bg-[#BF360C]' : 'bg-[#1B5E20]'}`}>
                  {l.type === 'debit' ? '−' : '+'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">
                    {l.type === 'debit' ? 'Udhaar' : 'Repayment'} · {inr(l.amount)}
                    {l.order_id && <span className="ml-2 text-[10px] text-ink/50 font-mono">#{l.order_id.slice(0, 8)}</span>}
                  </div>
                  <div className="text-[11px] text-ink/60">{fmtStamp(l.created_at, true)} · {l.note}</div>
                  {l.txn_serial && <div data-testid={`khata-ledger-serial-${l.id}`} className="font-mono text-[10px] text-ink/50">{l.txn_serial}</div>}
                </div>
                <div className="text-right text-[10px] text-ink/60">
                  Bal ₹{l.running_balance?.toFixed?.(2)}
                  {l.customer_phone && (
                    <a data-testid={`khata-ledger-wa-${l.id}`} href={receiptWaUrl({ ...l, store_name: l.store_name, customer_name: l.customer_name || detail?.customer_name })}
                      target="_blank" rel="noreferrer" className="mt-1 flex items-center justify-end gap-1 font-bold text-[#1B8A4A]">Receipt ↗</a>
                  )}
                </div>
              </div>
            ))}
            {(!detail?.ledger || !detail.ledger.length) && <div className="text-sm text-ink/40 py-4 text-center">No transactions yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ================================================================
//  🚀 BulkNudgeWizard — sequential rate-limit-safe WhatsApp blast
// ================================================================
function BulkNudgeWizard({ storeFilter, onClose }) {
  const [step, setStep]         = useState('preview');   // preview | sending | done
  const [batch, setBatch]       = useState(null);        // {nudges, total_count, total_balance, ...}
  const [selected, setSelected] = useState(new Set());
  const [minBalance, setMinBalance] = useState(1);
  const [minDays, setMinDays]   = useState(0);
  const [idx, setIdx]           = useState(0);           // current send index
  const [paused, setPaused]     = useState(false);
  const [sentPhones, setSentPhones] = useState(new Set());
  const [skippedPhones, setSkippedPhones] = useState(new Set());
  const [autoDelay, setAutoDelay] = useState(6);         // seconds between sends
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  // Load preview
  const loadBatch = useCallback(async () => {
    try {
      const b = await digitalKhata.bulkNudge({
        store_id: storeFilter || null,
        min_balance: minBalance,
        min_days_due: minDays,
      });
      setBatch(b);
      setSelected(new Set(b.nudges.map(n => n.customer_phone)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load bulk preview');
    }
  }, [storeFilter, minBalance, minDays]);
  useEffect(() => { loadBatch(); }, [loadBatch]);

  const activeNudges = useMemo(
    () => batch?.nudges?.filter(n => selected.has(n.customer_phone)) || [],
    [batch, selected],
  );

  const toggleOne = (phone) => {
    const next = new Set(selected);
    if (next.has(phone)) next.delete(phone); else next.add(phone);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === (batch?.nudges?.length || 0)) setSelected(new Set());
    else setSelected(new Set(batch?.nudges?.map(n => n.customer_phone)));
  };

  // ----- Sequential sender -----
  const sendCurrent = () => {
    const n = activeNudges[idx];
    if (!n) return;
    deliverWhatsApp({ phone: n.customer_phone, message: n.message, wa_url: n.wa_url });
    setSentPhones(prev => new Set(prev).add(n.customer_phone));
    // Schedule the next one
    setCountdown(autoDelay);
  };

  const skipCurrent = () => {
    const n = activeNudges[idx];
    if (n) setSkippedPhones(prev => new Set(prev).add(n.customer_phone));
    setIdx(i => i + 1);
    setCountdown(0);
  };

  useEffect(() => {
    if (step !== 'sending' || paused) return;
    if (countdown <= 0) return;
    timerRef.current = setTimeout(() => {
      setCountdown(c => c - 1);
    }, 1000);
    return () => clearTimeout(timerRef.current);
  }, [countdown, step, paused]);

  useEffect(() => {
    // When countdown hits 0 after a send, move to next
    if (step === 'sending' && !paused && sentPhones.size > 0 && countdown === 0) {
      const isLast = idx >= activeNudges.length - 1;
      if (isLast) { setStep('done'); return; }
      // Only advance if we actually just sent (not on initial 0)
      // We use a small guard: only advance if current phone is in sentPhones
      const curr = activeNudges[idx];
      if (curr && sentPhones.has(curr.customer_phone)) setIdx(i => i + 1);
    }
    /* eslint-disable-next-line */
  }, [countdown, step, paused]);

  const startSending = () => {
    if (activeNudges.length === 0) return toast.error('Select at least one customer');
    setIdx(0); setSentPhones(new Set()); setSkippedPhones(new Set()); setPaused(false);
    setStep('sending');
    // Auto-fire the first one after a tiny delay so the UI transitions
    setTimeout(() => sendCurrent(), 300);
  };

  const current = activeNudges[idx];
  const progress = activeNudges.length === 0 ? 0 : Math.round(((sentPhones.size + skippedPhones.size) / activeNudges.length) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-nudge-wizard">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#128C7E] to-[#25D366] text-white p-5 flex items-center justify-between rounded-t-3xl">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">Bulk WhatsApp Nudge</div>
            <div className="font-heading font-black text-2xl mt-0.5">
              {step === 'preview' && 'Who to remind?'}
              {step === 'sending' && `Sending ${idx + 1} of ${activeNudges.length}`}
              {step === 'done'    && '✅ Done!'}
            </div>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/15 hover:bg-white/25 p-2"><X className="w-5 h-5"/></button>
        </div>

        {/* ---- PREVIEW STEP ---- */}
        {step === 'preview' && (
          <div className="p-5">
            {!batch ? (
              <div className="text-center text-ink/60 py-8 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading customers…</div>
            ) : batch.nudges.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-14 h-14 mx-auto text-[#1B5E20]"/>
                <div className="font-heading font-black text-xl mt-3">Everyone is paid up! 🎉</div>
                <div className="text-ink/60 mt-1 text-sm">No customers currently owe you money above ₹{minBalance}.</div>
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink/50 font-bold">Only if balance ≥ ₹</label>
                    <input data-testid="bulk-min-balance" type="number" value={minBalance} onChange={e => setMinBalance(parseFloat(e.target.value) || 0)}
                      className="w-full mt-1 rounded-lg border border-border px-3 py-2 text-sm"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink/50 font-bold">Only if overdue ≥ days</label>
                    <input data-testid="bulk-min-days" type="number" value={minDays} onChange={e => setMinDays(parseInt(e.target.value) || 0)}
                      className="w-full mt-1 rounded-lg border border-border px-3 py-2 text-sm"/>
                  </div>
                </div>

                {/* Summary */}
                <div className="rounded-2xl bg-[#E8F5E9] p-4 mb-4">
                  <div className="text-sm text-[#1B5E20]">
                    <b>{selected.size}</b> customer{selected.size === 1 ? '' : 's'} · Total outstanding <b>₹{batch.nudges.filter(n => selected.has(n.customer_phone)).reduce((s, n) => s + n.balance, 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</b>
                  </div>
                  <div className="text-[10px] text-[#1B5E20]/70 mt-1">{batch.rate_limit_hint}</div>
                </div>

                {/* Message preview */}
                {activeNudges[0] && (
                  <details className="mb-4 rounded-lg border border-border">
                    <summary className="cursor-pointer p-3 text-sm font-semibold text-ink/80">📱 Preview message</summary>
                    <pre className="whitespace-pre-wrap text-xs p-3 border-t border-border bg-[#FBF8F1] font-mono">{activeNudges[0].message}</pre>
                  </details>
                )}

                {/* Customer list */}
                <div className="border border-border rounded-lg divide-y divide-border max-h-[40vh] overflow-y-auto">
                  <label className="flex items-center gap-2 px-3 py-2 bg-[#FBF8F1] cursor-pointer sticky top-0">
                    <input data-testid="bulk-select-all" type="checkbox" checked={selected.size === batch.nudges.length && batch.nudges.length > 0} onChange={toggleAll}/>
                    <span className="text-xs uppercase tracking-wider font-bold text-ink/60">Select all ({batch.nudges.length})</span>
                  </label>
                  {batch.nudges.map(n => (
                    <label key={n.customer_phone} data-testid={`bulk-pick-${n.customer_phone}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#FFF8E1]/40">
                      <input type="checkbox" checked={selected.has(n.customer_phone)} onChange={() => toggleOne(n.customer_phone)}/>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{n.customer_name}</div>
                        <div className="text-[11px] text-ink/50">📱 {n.customer_phone} · {n.store_name}{n.days_due > 0 ? ` · ${n.days_due}d overdue` : ''}</div>
                      </div>
                      <div className="font-heading font-black text-[#9A4500]">₹{n.balance.toLocaleString('en-IN', {maximumFractionDigits: 0})}</div>
                    </label>
                  ))}
                </div>

                {/* Delay */}
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <label className="text-xs text-ink/70 inline-flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5"/>
                    Delay between sends:
                    <select data-testid="bulk-delay" value={autoDelay} onChange={e => setAutoDelay(parseInt(e.target.value))} className="rounded border border-border px-2 py-1 text-xs">
                      <option value={3}>3s (fast)</option>
                      <option value={6}>6s (safe)</option>
                      <option value={10}>10s (extra safe)</option>
                      <option value={30}>30s (verified WA account)</option>
                    </select>
                  </label>
                  <button
                    data-testid="bulk-start-btn"
                    onClick={startSending}
                    disabled={selected.size === 0}
                    className="rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:opacity-95 text-white px-6 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40"
                  >
                    <Zap className="w-4 h-4"/>Start nudging {selected.size} customer{selected.size === 1 ? '' : 's'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ---- SENDING STEP ---- */}
        {step === 'sending' && current && (
          <div className="p-5">
            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-ink/60 mb-1">
                <span>{sentPhones.size} sent · {skippedPhones.size} skipped</span>
                <span>{progress}% complete</span>
              </div>
              <div className="h-2 rounded-full bg-border overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#25D366] to-[#128C7E] transition-all" style={{ width: `${progress}%` }}/>
              </div>
            </div>

            {/* Current customer card */}
            <div className="rounded-2xl border-2 border-[#128C7E] bg-[#F1F8E9] p-5">
              <div className="text-[10px] uppercase tracking-wider text-[#128C7E] font-bold">Nudging now</div>
              <div className="mt-1 flex items-start gap-4 flex-wrap">
                <div>
                  <div className="font-heading font-black text-2xl">{current.customer_name}</div>
                  <div className="text-sm text-ink/60">📱 +91 {current.customer_phone} · {current.store_name}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[10px] uppercase tracking-wider text-ink/50">Balance</div>
                  <div className="font-heading font-black text-3xl text-[#9A4500]">₹{current.balance.toLocaleString('en-IN', {maximumFractionDigits: 0})}</div>
                </div>
              </div>

              {countdown > 0 ? (
                <div className="mt-4 rounded-lg bg-white border border-[#128C7E]/40 p-3 flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-[#128C7E]"/>
                  <div className="flex-1">WhatsApp opened for <b>{current.customer_name}</b> — tap <b>Send</b> in that tab. Next in <b>{countdown}s</b>…</div>
                </div>
              ) : (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs font-semibold text-ink/60">📱 Message content</summary>
                  <pre className="whitespace-pre-wrap text-[11px] mt-2 p-3 bg-white rounded border border-border font-mono">{current.message}</pre>
                </details>
              )}
            </div>

            {/* Controls */}
            <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
              <button data-testid="bulk-pause" onClick={() => setPaused(p => !p)} className="rounded-full border border-border bg-white hover:bg-muted px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5">
                {paused ? <><Play className="w-4 h-4"/>Resume</> : <><Pause className="w-4 h-4"/>Pause</>}
              </button>
              <div className="flex gap-2">
                <button data-testid="bulk-skip" onClick={skipCurrent} className="rounded-full border border-border bg-white hover:bg-muted px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5"><SkipForward className="w-4 h-4"/>Skip</button>
                <button data-testid="bulk-resend" onClick={sendCurrent} className="rounded-full bg-[#128C7E] hover:bg-[#075E54] text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5"><Send className="w-4 h-4"/>Re-open WhatsApp</button>
              </div>
            </div>
          </div>
        )}

        {/* ---- DONE STEP ---- */}
        {step === 'done' && (
          <div className="p-5 text-center">
            <CheckCircle2 className="w-20 h-20 mx-auto text-[#1B5E20]"/>
            <div className="font-heading font-black text-2xl mt-3">All done!</div>
            <div className="text-ink/70 mt-2 text-sm">
              <b>{sentPhones.size}</b> reminders sent · <b>{skippedPhones.size}</b> skipped
            </div>
            <div className="mt-4 rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] p-3 text-xs text-[#1B5E20]">
              💡 Tip: run this on the 1st of every month for maximum recovery. Customers pay 3× faster when reminded before 5th.
            </div>
            <button onClick={onClose} className="mt-5 rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-6 py-2.5 text-sm font-bold">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ================================================================
//  ⏰ ScheduleConfigCard — auto-schedule the monthly bulk nudge
// ================================================================
function ScheduleConfigCard() {
  const [sch, setSch] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setSch(await digitalKhata.getSchedule()); setDirty(false); } catch { /* not owner or first-time */ } };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await digitalKhata.saveSchedule({
        enabled:      !!sch.enabled,
        day_of_month: parseInt(sch.day_of_month || 1),
        hour_of_day:  parseInt(sch.hour_of_day || 10),
        min_balance:  parseFloat(sch.min_balance || 100),
        min_days_due: parseInt(sch.min_days_due || 0),
      });
      toast.success(sch.enabled ? '⏰ Auto-schedule saved!' : 'Auto-schedule turned off');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const nextRunLabel = sch?.next_run
    ? new Date(sch.next_run).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ' at ' + new Date(sch.next_run).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div data-testid="khata-schedule-card" className="rounded-2xl border-2 border-[#B39DDB] bg-gradient-to-r from-[#F3E5F5] to-white p-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-full bg-[#4527A0] text-white grid place-items-center flex-shrink-0">⏰</div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#4527A0]">Auto-schedule</div>
          <div className="font-heading font-bold text-lg">Monthly bulk nudge reminder</div>
          <p className="text-xs text-ink/60 mt-1">
            BharatCart will remind you to send WhatsApp nudges to all pending-balance customers — automatically, on the day &amp; time you choose.
            {sch?.enabled && nextRunLabel && <> Next reminder: <b className="text-[#4527A0]">{nextRunLabel}</b>.</>}
          </p>
        </div>
        <label data-testid="khata-schedule-toggle" className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!sch?.enabled} onChange={e => { setSch(s => ({ ...s, enabled: e.target.checked })); setDirty(true); }} className="w-5 h-5 accent-[#4527A0]"/>
          <span className="text-sm font-semibold">{sch?.enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      {sch?.enabled !== undefined && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <label className="flex flex-col gap-1">
            <span className="text-ink/60">Day of month</span>
            <select data-testid="khata-schedule-day" disabled={!sch.enabled} value={sch.day_of_month || 1} onChange={e => { setSch({ ...sch, day_of_month: parseInt(e.target.value) }); setDirty(true); }} className="rounded border border-border px-2 py-1.5 bg-white disabled:opacity-50">
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-ink/60">Hour (24h)</span>
            <select data-testid="khata-schedule-hour" disabled={!sch.enabled} value={sch.hour_of_day ?? 10} onChange={e => { setSch({ ...sch, hour_of_day: parseInt(e.target.value) }); setDirty(true); }} className="rounded border border-border px-2 py-1.5 bg-white disabled:opacity-50">
              {Array.from({ length: 24 }, (_, i) => i).map(h => <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-ink/60">Only if bal ≥ ₹</span>
            <input data-testid="khata-schedule-minbal" disabled={!sch.enabled} type="number" value={sch.min_balance ?? 100} onChange={e => { setSch({ ...sch, min_balance: parseFloat(e.target.value) || 0 }); setDirty(true); }} className="rounded border border-border px-2 py-1.5 disabled:opacity-50"/>
          </label>
          <label className="flex items-end">
            {dirty ? (
              <button data-testid="khata-schedule-save" disabled={busy} onClick={save} className="w-full rounded-full bg-[#4527A0] hover:bg-[#311B92] text-white px-3 py-2 text-xs font-bold disabled:opacity-60">
                {busy ? 'Saving…' : 'Save schedule'}
              </button>
            ) : (
              <div className="text-[11px] text-ink/50 italic">All saved ✓</div>
            )}
          </label>
        </div>
      )}
    </div>
  );
}

